import { useEffect, useState } from 'react';
import FeatureTour, {
  hasSeenTour,
  markTourSeen,
  resetTourSeen,
  type TourStep,
} from './FeatureTour';

/**
 * FirstRunTour — the welcome walkthrough for brand-new users.
 *
 * Auto-launches once per user account when they first land in the app.
 * Each step pins a spotlight on a real nav item or widget with a plain-language
 * explainer, so a first-time user sees the shape of the whole platform in
 * under a minute. Replayable anytime via `data-testid="replay-tour"` (used in
 * the account menu).
 *
 * Accessibility inherits FeatureTour's: labelled dialog, Esc to dismiss,
 * focus management, live-region step counter.
 */

const ORG_TOUR: TourStep[] = [
  {
    target: '[data-tour="/app/discover"]',
    title: 'Find your next grant',
    body: 'Discover searches real grants and RFPs and scores each one against your organization, so you chase the right opportunities first.',
  },
  {
    target: '[data-tour="/app/pipeline"]',
    title: 'Work every opportunity',
    body: 'Pipeline tracks each opportunity from first look to submitted proposal. Nothing slips through the cracks.',
  },
  {
    target: '[data-tour="/app/proposals"]',
    title: 'Draft with Quill',
    body: 'Quill writes proposal drafts in your voice, pulling from your Organization DNA so you never start from a blank page.',
  },
  {
    target: '[data-tour="/app/relationships"]',
    title: 'Cultivate your funders',
    body: 'Relationships is your built-in funder CRM: a cultivation pipeline, interaction timelines, follow-up reminders, CSV import, and automatic email capture.',
  },
  {
    target: '[data-tour="/app/readiness"]',
    title: 'Know you’re ready',
    body: 'Readiness scores your organization across the things funders actually check — and tells you exactly what to fix.',
  },
];

const INDIVIDUAL_TOUR: TourStep[] = [
  {
    target: '[data-tour="/app/individual/discover"]',
    title: 'Find your funding',
    body: 'Discover matches scholarships, grants, and fellowships to your profile, with a plain-language breakdown of why each one fits.',
  },
  {
    target: '[data-tour="/app/individual/applications"]',
    title: 'Track applications',
    body: 'Every application you start lands here with its deadline, status, and next step.',
  },
  {
    target: '[data-tour="/app/individual/profile"]',
    title: 'Your profile powers everything',
    body: 'The more complete your profile, the better your matches and the stronger Quill’s essay drafts.',
  },
];

/** Steps for the Relationships page itself — CRM feature walkthrough. */
export const RELATIONSHIPS_TOUR: TourStep[] = [
  {
    target: '[data-tour="follow-ups"]',
    title: 'Never miss a follow-up',
    body: 'Overdue and upcoming reminders surface here and on your Dashboard. Complete or snooze them with one tap.',
  },
  {
    target: '[data-tour="cultivation-pipeline"]',
    title: 'Your cultivation pipeline',
    body: 'Move funders from Identification to Stewardship. Click a card to open the contact drawer, change stages, and log every touchpoint.',
  },
  {
    target: '[data-tour="add-contact"]',
    title: 'Add anyone in seconds',
    body: 'Capture a new funder, program officer, or partner — plus any custom fields your team defines.',
  },
  {
    target: '[data-tour="import-contacts"]',
    title: 'Bring your spreadsheet',
    body: 'Import a CSV of contacts. Duplicates are flagged against your list before anything is imported, and merging is one click.',
  },
  {
    target: '[data-tour="email-capture"]',
    title: 'Emails land themselves',
    body: 'Connect your Gmail and threads with your contacts are logged to their timelines automatically.',
  },
];

const RELATIONSHIPS_SEEN_KEY = 'gn-crm-tour-seen-v1';

export function hasSeenRelationshipsTour(userId?: string): boolean {
  const key = userId ? `${RELATIONSHIPS_SEEN_KEY}:${userId}` : RELATIONSHIPS_SEEN_KEY;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function markRelationshipsTourSeen(userId?: string): void {
  const key = userId ? `${RELATIONSHIPS_SEEN_KEY}:${userId}` : RELATIONSHIPS_SEEN_KEY;
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* noop */
  }
}

export function resetRelationshipsTourSeen(userId?: string): void {
  const key = userId ? `${RELATIONSHIPS_SEEN_KEY}:${userId}` : RELATIONSHIPS_SEEN_KEY;
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

export default function FirstRunTour({ isIndividual, userId }: { isIndividual: boolean; userId?: string }) {
  const [open, setOpen] = useState(false);

  // Auto-launch once per account on first arrival.
  useEffect(() => {
    if (hasSeenTour(userId)) return;
    // Small delay so the layout paints before the spotlight pins.
    const t = window.setTimeout(() => setOpen(true), 900);
    return () => window.clearTimeout(t);
  }, [userId]);

  // Replay hook: the account menu dispatches this event.
  useEffect(() => {
    const replay = () => {
      resetTourSeen(userId);
      setOpen(true);
    };
    window.addEventListener('gn:replay-tour', replay);
    return () => window.removeEventListener('gn:replay-tour', replay);
  }, [userId]);

  return (
    <FeatureTour
      steps={isIndividual ? INDIVIDUAL_TOUR : ORG_TOUR}
      open={open}
      userId={userId}
      onClose={() => {
        setOpen(false);
        markTourSeen(userId);
      }}
    />
  );
}

/** Fires the tour replay event (listened for by FirstRunTour).
 *  On the Relationships page this replays the CRM walkthrough instead. */
export function replayTour(): void {
  if (window.location.pathname.startsWith('/app/relationships')) {
    window.dispatchEvent(new Event('gn:replay-crm-tour'));
  } else {
    window.dispatchEvent(new Event('gn:replay-tour'));
  }
}
